import { Home, HardHat, Leaf, Hammer, Building2, Truck } from "lucide-react";
import { Reveal, Hud } from "./Reveal";
import { SectionStrip } from "./SectionStrip";

const PHOTO_SERVICES = [
  { img: "/assets/svc-furniture-ba.jpg", alt: "Before and after of a porch furniture and mattress removal — sofa, dresser, and mattress hauled away by GODSPEED Junk Hauling in Kennesaw, GA", title: "Furniture & Mattresses", desc: "Sofas, sectionals, beds, dressers, and dining sets — we carry them out carefully, protect your walls and floors, and have them gone the same day you call. Upstairs, downstairs, tight hallways — we've got the crew and the gear for it.", slug: "furniture" },
  { img: "/assets/svc-appliance-ba.jpg", alt: "Before and after of a garage appliance removal — refrigerator and washer hauled away by GODSPEED Junk Hauling in Kennesaw, GA", title: "Appliances", desc: "Refrigerators, washers, dryers, ovens, and freezers — we disconnect, haul, and make sure working units get donated or recycled whenever possible. We handle the heavy lifting and awkward doorways so you don't touch a thing.", slug: "appliance" },
  { img: "/assets/svc-garage-ba.jpg", alt: "Before and after of a garage cleanout by GODSPEED Junk Hauling in Cobb County, GA", title: "Garage / Basement / Attic", desc: "From one cluttered corner to a whole floor — we sort, load, and haul everything, then sweep the space clean so it's ready for whatever's next. We even set aside anything you want to keep before the truck pulls away.", slug: "cleanouts" },
];

const ICON_SERVICES = [
  { icon: Home, title: "Estate & Foreclosure Cleanouts", desc: "Full-property cleanouts handled with care and discretion — we work on your timeline, set aside keepsakes, and leave the home broom-clean for listing. Families and agents trust us with sensitive situations, and we treat every item with respect.", slug: "estate" },
  { icon: HardHat, title: "Renovation & Construction Debris", desc: "Drywall, lumber, tile, cabinets, and old fixtures — we keep your job site clear and moving with flexible pickups that fit your build schedule. One-off hauls or recurring runs, we keep crews working.", slug: "construction" },
  { icon: Leaf, title: "Yard & Storm Debris", desc: "Branches, brush, fencing, and storm mess — we rake it up, load it out, and leave your yard looking like we were never there. Fence panels, old playsets, and storm piles gone in one visit.", slug: "yard" },
  { icon: Hammer, title: "Hot Tub & Shed Teardown", desc: "Hot tubs, sheds, and playsets — we disconnect, break them down safely, and haul off every last piece. You point, we handle the rest, including the leftover debris in the same visit.", slug: "teardown" },
  { icon: Building2, title: "Commercial & Property Managers", desc: "Office cleanouts, tenant turnovers, and set-outs — fast, documented, and dependable for managers who need it done right and done now. Before-and-after photos and invoicing available on every account.", slug: "commercial" },
];

export const Services = () => (
  <section id="services" data-testid="services-section" className="py-24 md:py-32">
    <div className="mx-auto max-w-7xl px-5 md:px-8">
      <Reveal>
        <p data-testid="services-label" className="mb-6 inline-flex items-stretch border-2 border-gold">
          <span className="bg-gold px-3 py-1.5 font-heading text-base uppercase tracking-wide text-ink">Service 01</span>
          <span className="px-4 py-1.5 font-body text-xs font-bold uppercase tracking-[0.2em] text-gold">Junk Removal</span>
        </p>
        <Hud>What We Haul Away</Hud>
        <h2 className="mt-5 font-heading text-5xl uppercase leading-[0.9] tracking-tight text-white md:text-7xl">
          Junk Removal:
          <br />
          <span className="text-gold">We Haul It Away.</span>
        </h2>
        <p className="mt-5 max-w-xl text-base font-medium capitalize leading-relaxed text-warmgray md:text-lg">
          Unwanted junk, gone for good. If it's legal and we can lift it, we remove it and dispose of it responsibly — for
          homes and businesses across Kennesaw, Cobb County, and North Metro Atlanta.
        </p>
      </Reveal>

      <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3">
        {PHOTO_SERVICES.map((s, i) => (
          <Reveal key={s.slug} delay={i * 0.08} className="h-full">
            <div data-testid={`service-card-${s.slug}`} className="grit-card group h-full">
              <div className="relative aspect-[5/3] overflow-hidden border-b-2 border-coal bg-ink p-1">
                <img src={s.img} alt={s.alt} loading="lazy" decoding="async" className="h-full w-full object-contain transition-transform duration-500 group-hover:scale-[1.03]" />
                <div className="hazard-thin absolute bottom-0 left-0 h-1.5 w-full" aria-hidden="true" />
              </div>
              <div className="p-7">
                <h3 className="font-heading text-2xl uppercase tracking-wide text-white transition-colors duration-200 group-hover:text-gold md:text-3xl">
                  {s.title}
                </h3>
                <p className="mt-3 text-sm capitalize leading-relaxed text-warmgray">{s.desc}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-5">
        {ICON_SERVICES.map((s, i) => (
          <Reveal key={s.slug} delay={i * 0.06} className="h-full">
            <div data-testid={`service-card-${s.slug}`} className="grit-card group flex h-full flex-col p-6">
              <span className="flex h-12 w-12 items-center justify-center border-2 border-gold/40 bg-ink transition-all duration-200 group-hover:border-gold group-hover:bg-gold/10">
                <s.icon size={22} strokeWidth={1.6} className="text-gold" />
              </span>
              <h3 className="mt-5 font-heading text-xl uppercase leading-tight tracking-wide text-white transition-colors duration-200 group-hover:text-gold">
                {s.title}
              </h3>
              <p className="mt-2.5 text-sm capitalize leading-relaxed text-warmgray">{s.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>

      <SectionStrip
        icon={Truck}
        title="Don't See It Listed?"
        text="If it's legal and we can lift it, it's gone — appliances, exercise equipment, electronics, and just about everything in between. One call handles the whole pile."
        testid="services-strip"
      />
    </div>
  </section>
);
